import { Edit } from "@mui/icons-material";
import { Box, CardContent, Stack, Typography } from "@mui/material";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { ResponsiveButtonLink } from "components/Button";
import { CardOutline } from "components/Card";
import { MainLayoutContainer } from "components/Layout";
import { RequirementBox } from "components/Requirements";
import { TagManager } from "components/TagManager";
import { useGetAcademicYearsByIdLazyQuery } from "graphql/__generated__/hooks";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Requirement } from "types/requirement";

export const AcademicYearDetail = () => {
  const { academicYearId, organizationId } = useParams();
  const [getAcademicYearsById, { data: academicYearData }] =
    useGetAcademicYearsByIdLazyQuery();

  const academicYear = useMemo(() => {
    if (academicYearData?.academic_year_by_pk) {
      return academicYearData.academic_year_by_pk;
    }
  }, [academicYearData]);

  const displayEditButton = useMemo(
    () => !(organizationId && academicYear?.program_id === null),
    [organizationId, academicYear],
  );

  useEffect(() => {
    if (academicYearId) {
      getAcademicYearsById({ variables: { academic_year_id: academicYearId } });
    }
  }, [academicYearId]);

  return (
    <MainLayoutContainer
      title={academicYear?.name ?? "Program"}
      buttonActions={
        displayEditButton && (
          <ResponsiveButtonLink
            icon={<Edit />}
            to={
              organizationId
                ? `/organization/${organizationId}/yearly-curriculum/${academicYearId}/edit`
                : `/academic-year/edit/${academicYearId}`
            }
          >
            Edit
          </ResponsiveButtonLink>
        )
      }
      breadcrumb={
        organizationId
          ? BREADCRUMB_PAGE.YEARLY_CURRICULUM
          : BREADCRUMB_PAGE.ACADEMIC_YEAR
      }
    >
      <Box mt={2}>
        <CardOutline>
          <CardContent>
            <Typography variant="body1" component="div" sx={{ mt: 1 }}>
              <strong>Name:</strong> {academicYear?.name}
            </Typography>

            {academicYear?.start_date && academicYear?.end_date && (
              <Typography variant="body1" component="div" sx={{ mt: 1 }}>
                <strong>Start Date:</strong> {academicYear?.start_date} -{" "}
                <strong>End Date:</strong> {academicYear?.end_date}
              </Typography>
            )}

            <Box mt={2}>
              <Stack spacing={1} direction={"row"}>
                <Typography gutterBottom component="div" fontWeight={600}>
                  Tags:
                </Typography>
                {academicYear?.tags && (
                  <TagManager
                    tags={academicYear?.tags.map((tag) => tag.tag)}
                    isEditing={false}
                  />
                )}
              </Stack>
            </Box>

            <Box mt={2}>
              <Typography gutterBottom component="div" fontWeight={600}>
                Requirements
              </Typography>

              {academicYear?.requirements.map((requirement) => (
                <RequirementBox
                  key={requirement.requirement.requirement_id}
                  requirement={requirement.requirement as Requirement}
                />
              ))}
            </Box>
          </CardContent>
        </CardOutline>
      </Box>
    </MainLayoutContainer>
  );
};
